import React from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { JobFile } from 'types';
import FileDownload from './FileDownload';
import FileDelete from './FileDelete';
import classes from './index.module.scss';

/**
 * Base props interface for FileList components
 */
interface Props {
	/** Array of job files to display */
	files: JobFile[];
}

/**
 * Downloadable component
 * Renders a scrollable list of files that can be downloaded
 * Uses custom scrollbar styling
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const Downloadable = ({ files }: Props) => (
	<Scrollbars renderThumbVertical={(props) => <div {...props} className={classes['vertical-scroll-thumb']} />}>
		{/* Map through files and render a FileDownload component for each */}
		{files.map((file) => (
			<FileDownload key={file.id} file={file} />
		))}
	</Scrollbars>
);

/**
 * Deletable component
 * Renders a scrollable list of files that can be deleted
 * Uses custom scrollbar styling
 * Requires jobId for delete operations
 *
 * @param {Props & { jobId: number }} props - Component props with jobId
 * @returns {JSX.Element} - Rendered component
 */
const Deletable = ({ files, jobId }: Props & { jobId: number }) => (
	<Scrollbars renderThumbVertical={(props) => <div {...props} className={classes['vertical-scroll-thumb']} />}>
		{/* Map through files and render a FileDelete component for each */}
		{files.map((file) => (
			<FileDelete key={file.id} file={file} jobId={jobId} />
		))}
	</Scrollbars>
);

/**
 * Export both components as a single object
 * This allows importing and using them as FileList.Downloadable and FileList.Deletable
 */
export default {
	Downloadable,
	Deletable,
};
